import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { CommonService } from 'src/app/shared/common.service';
import { ILabelSeeding } from '../../../models/settings.models';
import { SettingsService } from '../../../services/settings.services';

export type LabelSeedSource = 'product_categories' | 'company_groups';

// Switches for creating labels from the ERP, one per source. Off by default:
// a tenant decides whether its catalogue belongs in the inbox.
@Component({
  selector: 'app-label-seeding',
  templateUrl: './label-seeding.component.html',
  styleUrls: ['./label-seeding.component.scss'],
})
export class LabelSeedingComponent implements OnInit {

  /** Turning a source on created labels, so the list above needs refetching. */
  @Output() seeded = new EventEmitter<void>();

  readonly sources: LabelSeedSource[] = ['product_categories', 'company_groups'];

  flags: Record<LabelSeedSource, boolean> = { product_categories: false, company_groups: false };
  loaded = false;
  saving: LabelSeedSource | null = null;

  constructor(
    private settingsService: SettingsService,
    private commonService: CommonService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.settingsService.getLabelSeeding().subscribe({
      next: (response) => {
        this.flags = this.flagsFrom(response.object);
        this.loaded = true;
      },
      error: () => { this.loaded = true; },
    });
  }

  toggle(source: LabelSeedSource, enabled: boolean): void {
    const previous = this.flags;
    this.flags = { ...previous, [source]: enabled };
    this.saving = source;

    this.settingsService.updateLabelSeeding({ [source]: enabled }).subscribe({
      next: (response) => {
        this.saving = null;
        this.flags = this.flagsFrom(response.object);
        const created = response.object?.created?.[source];
        if (created === undefined) {
          return;
        }
        this.commonService.showToast(
          created
            ? this.translate.instant('settings.label_seeding.created', { count: created })
            : this.translate.instant('settings.label_seeding.created_none'),
          created ? 'SUCCESS' : 'INFO',
        );
        if (created) {
          this.seeded.emit();
        }
      },
      // Put back, so the switch never shows a state the server did not store.
      error: () => {
        this.saving = null;
        this.flags = previous;
      },
    });
  }

  private flagsFrom(data: ILabelSeeding | undefined): Record<LabelSeedSource, boolean> {
    return {
      product_categories: !!data?.product_categories,
      company_groups: !!data?.company_groups,
    };
  }
}
